import React, { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import _ from 'lodash'
import CheckboxOption from './panels/CheckboxOption'
import buildCategoryTree, { normalizeHierMap } from './buildCategoryTree'
import { CloseIcon, SearchSmall } from './icons'

// Full-height slide-up drawer (reusing the existing .drawer.slide-up chrome
// per the user's preference). Internally the body is an accordion: one row
// per facet, expanded inline. All facets are staged at once; "Show Lots"
// commits everything in a single model update.
//
// Each expanded section includes a search input (matching the desktop
// panel pattern) so users can narrow the list. Category renders as a
// nested parent/child tree using the same buildCategoryTree helper.

// DEV-2980: the storefront's filter sheet group (.mno-filter-group): an
// uppercase head with its (count) and a chevron that turns when open.
function FacetSection({ title, count, isOpen, onToggle, children }) {
  return (
    <div className="mno-filter-group">
      <button type="button" className="mno-filter-head" aria-expanded={isOpen} onClick={onToggle}>
        <span>{title}{count > 0 && <span className="mno-filter-count">({count})</span>}</span>
        <svg className="mno-chev" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 2.5 8 6l-3.5 3.5" />
        </svg>
      </button>
      {isOpen && <div className="mno-filter-body">{children}</div>}
    </div>
  )
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <label className="mno-search">
      <SearchSmall />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={placeholder}
      />
    </label>
  )
}

export default function MobileFilterDrawer({ model, changeState, search, onClose }) {
  const filters = model.filters
  const groups = (model.results && model.results.results && model.results.results.groups) || {}

  // Applied departments map, keyed by the full "Parent >> Child" pair
  // (same keyspace the strict-hierarchy tree uses) — see buildCategoryTree.js.
  const normalizedDept = useMemo(
    () => normalizeHierMap(filters.departments.selected || {}),
    [filters.departments.selected]
  )

  const [stagedDept, setStagedDept] = useState(normalizedDept)
  const [stagedOrgs, setStagedOrgs] = useState({ ...((filters.orgs && filters.orgs.selected && filters.orgs.selected.orgs) || {}) })
  const [stagedCauses, setStagedCauses] = useState({ ...((filters.orgs && filters.orgs.selected && filters.orgs.selected.causes) || {}) })

  const [openSection, setOpenSection] = useState('category')
  const [catQuery, setCatQuery] = useState('')
  const [orgQuery, setOrgQuery] = useState('')
  const [causeQuery, setCauseQuery] = useState('')

  function toggleSection(id) { setOpenSection((prev) => (prev === id ? null : id)) }

  const rawDeptLocked = (filters.departments && filters.departments.locked) || {}
  const deptLocked = useMemo(() => normalizeHierMap(rawDeptLocked), [rawDeptLocked])
  const orgsLocked = (filters.orgs && filters.orgs.orgs_locked) || {}

  // Counts a parent in parent mode as 1 (not "1 + every child"), since
  // children entries are dropped from staged when parent is selected.
  const deptCount = useMemo(() => Object.keys(stagedDept).filter((k) => stagedDept[k]).length, [stagedDept])
  const orgsCount = useMemo(() => Object.keys(stagedOrgs).filter((k) => stagedOrgs[k]).length, [stagedOrgs])
  const causesCount = useMemo(() => Object.keys(stagedCauses).filter((k) => stagedCauses[k]).length, [stagedCauses])

  function toggleStaged(setter) {
    return (name) => setter((prev) => ({ ...prev, [name]: !prev[name] }))
  }

  // Category parent / child — strict-hierarchy model:
  // - staged map keys are the child's full "Parent >> Child" pair (unique
  //   across parents, unlike the short name — see buildCategoryTree.js)
  // - parent visual state is derived: all-children-checked = parent on
  // - clicking the parent flips all (non-locked) children to the
  //   opposite of "all currently checked"
  function parentChecked(node) {
    return node.children.length > 0 && node.children.every((c) => stagedDept[c.name])
  }

  function toggleCategoryParent(node) {
    const allChecked = parentChecked(node)
    setStagedDept((prev) => {
      const next = { ...prev }
      for (const child of node.children) {
        if (deptLocked[child.name]) continue
        next[child.name] = !allChecked
      }
      return next
    })
  }

  // Category tree (parent → children) + query-filtered version.
  // Build off the raw locked map so flat-only locks contribute correctly;
  // the tree itself converts those to short-name children.
  const categoryTree = useMemo(
    () => buildCategoryTree(groups.categories || {}, rawDeptLocked),
    [groups.categories, rawDeptLocked]
  )
  const filteredCategoryTree = useMemo(() => {
    const q = catQuery.trim().toLowerCase()
    if (!q) return categoryTree
    const out = []
    for (const node of categoryTree) {
      const parentMatches = node.name.toLowerCase().includes(q)
      const matchingKids = node.children.filter((c) =>
        c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q)
      )
      if (parentMatches || matchingKids.length > 0) {
        const kidsToShow = parentMatches && matchingKids.length === 0 ? node.children : matchingKids
        out.push({ ...node, children: kidsToShow })
      }
    }
    return out
  }, [categoryTree, catQuery])

  // Charity + cause: simple substring filter.
  const filteredOrgs = useMemo(() => {
    const q = orgQuery.trim().toLowerCase()
    return Object.keys(groups.org_names || {})
      .filter((name) => !q || name.toLowerCase().includes(q))
  }, [groups.org_names, orgQuery])
  const filteredCauses = useMemo(() => {
    const q = causeQuery.trim().toLowerCase()
    return Object.keys(groups.cause || {})
      .filter((name) => !q || name.toLowerCase().includes(q))
  }, [groups.cause, causeQuery])

  function clearAll() {
    setStagedDept({ ...deptLocked })
    setStagedOrgs({ ...orgsLocked })
    setStagedCauses({})
  }

  function applyAll() {
    const updated = _.cloneDeep(model)
    updated.filters.departments.selected = { ...stagedDept }
    if (!updated.filters.orgs.selected) updated.filters.orgs.selected = {}
    updated.filters.orgs.selected.orgs = { ...stagedOrgs }
    updated.filters.orgs.selected.causes = { ...stagedCauses }

    updated.filters.pagination.page = 1
    updated.filters.departments.on = false
    updated.filters.orgs.on = false
    updated.filters.orgs.causes_on = false
    updated.filters.themes.on = false

    changeState(updated)
    search({ m: updated })
    document.dispatchEvent(new CustomEvent('update-listing-filters', {
      detail: { filters: updated.filters }
    }))
    onClose()
  }

  const totalStagedCount = deptCount + orgsCount + causesCount

  // DEV-2980: the storefront's filter sheet (mno-sheet), portaled to <body>.
  return createPortal(
    <div className="sfc">
      <div className="mno-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="mno-sheet" role="dialog" aria-label="Filter lots">
        <div className="mno-sheet-head">
          <button type="button" className="mno-close" onClick={onClose} aria-label="Close filters">
            <CloseIcon />
          </button>
          <h2 className="mno-sheet-title">Filters</h2>
          <button type="button" className="mno-sheet-clear" onClick={clearAll}>
            Clear all
          </button>
        </div>

        <div className="mno-sheet-body">
          <FacetSection
            title="By Category"
            count={deptCount}
            isOpen={openSection === 'category'}
            onToggle={() => toggleSection('category')}
          >
            <SearchInput value={catQuery} onChange={setCatQuery} placeholder="Search categories" />
            {filteredCategoryTree.length === 0
              ? <p className="mno-opt-empty">{catQuery ? 'No categories match' : 'No categories available'}</p>
              : filteredCategoryTree.map((node) => (
                  <React.Fragment key={node.name}>
                    <CheckboxOption
                      parent
                      label={node.name}
                      count={null}
                      checked={parentChecked(node)}
                      locked={false}
                      onToggle={() => toggleCategoryParent(node)}
                    />
                    {node.children.map((child) => (
                      <CheckboxOption
                        key={child.name}
                        child
                        label={child.label}
                        count={child.count}
                        checked={!!stagedDept[child.name]}
                        locked={!!deptLocked[child.name]}
                        onToggle={() => toggleStaged(setStagedDept)(child.name)}
                      />
                    ))}
                  </React.Fragment>
                ))}
          </FacetSection>

          <FacetSection
            title="By Charity"
            count={orgsCount}
            isOpen={openSection === 'charity'}
            onToggle={() => toggleSection('charity')}
          >
            <SearchInput value={orgQuery} onChange={setOrgQuery} placeholder="Search charities" />
            {filteredOrgs.length === 0
              ? <p className="mno-opt-empty">{orgQuery ? 'No charities match' : 'No charities available'}</p>
              : filteredOrgs.map((name) => (
                <CheckboxOption
                  key={name}
                  label={name}
                  count={(groups.org_names || {})[name]}
                  checked={!!stagedOrgs[name]}
                  locked={!!orgsLocked[name]}
                  onToggle={() => toggleStaged(setStagedOrgs)(name)}
                />
              ))}
          </FacetSection>

          <FacetSection
            title="By Cause"
            count={causesCount}
            isOpen={openSection === 'cause'}
            onToggle={() => toggleSection('cause')}
          >
            <SearchInput value={causeQuery} onChange={setCauseQuery} placeholder="Search causes" />
            {filteredCauses.length === 0
              ? <p className="mno-opt-empty">{causeQuery ? 'No causes match' : 'No causes available'}</p>
              : filteredCauses.map((name) => (
                <CheckboxOption
                  key={name}
                  label={name}
                  count={(groups.cause || {})[name]}
                  checked={!!stagedCauses[name]}
                  onToggle={() => toggleStaged(setStagedCauses)(name)}
                />
              ))}
          </FacetSection>
        </div>

        <div className="mno-sheet-foot">
          <button type="button" className="mno-apply" onClick={applyAll}>
            Show Lots{totalStagedCount > 0 ? ` · ${totalStagedCount}` : ''}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
